import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { NAV_LINKS } from "@/lib/site-data";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On legal pages, header always shows solid variant (there is no dark hero behind).
  const forceSolid = !isHome;
  const solid = scrolled || forceSolid;

  const goSection = (id) => {
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const headerBg = solid
    ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70"
    : "bg-transparent";
  const logoMain = solid ? "text-slate-950" : "text-white";
  const linkColor = solid
    ? "text-slate-700 hover:text-emerald-800"
    : "text-slate-200 hover:text-white";
  const ctaCls = solid
    ? "bg-slate-950 text-white hover:bg-emerald-800"
    : "bg-emerald-700 text-white hover:bg-emerald-600";
  const toggleColor = solid || open ? "text-slate-950" : "text-white";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <div className="container-narrow flex items-center justify-between h-16 md:h-20">
        <button
          data-testid="nav-logo"
          onClick={goHome}
          className="flex items-baseline gap-2 group"
        >
          <span className={`font-serif text-xl md:text-2xl tracking-tight transition-colors ${logoMain}`}>
            Dream<span className="text-emerald-500">Axis</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className={`text-sm transition-colors ${linkColor}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center">
          <button
            data-testid="nav-cta"
            onClick={() => goSection("contact")}
            className={`transition-colors px-5 py-2.5 text-sm font-medium ${ctaCls}`}
          >
            Start a conversation
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`md:hidden p-2 transition-colors ${toggleColor}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-slate-200 bg-white"
        >
          <div className="container-narrow py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => goSection(l.id)}
                className="text-left py-3 text-slate-800 border-b border-slate-100"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={() => goSection("contact")}
              className="bg-slate-950 text-white px-5 py-3 text-sm font-medium mt-3"
            >
              Start a conversation
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
